/**
 * ContentManager Component - Manages workshop modules, folders, and content
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { 
  Plus, Edit, Trash2, FolderPlus, FileText, ChevronDown, ChevronUp 
} from 'lucide-react';
import { getContentTypeIcon } from './WorkshopUtils';

export const ContentManager = ({ 
  workshop,
  canEdit,
  onAddModule,
  onEditModule,
  onDeleteModule,
  onAddFolder,
  onEditFolder,
  onDeleteFolder,
  onAddContent,
  onEditContent,
  onDeleteContent
}) => {
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedFolders, setExpandedFolders] = useState({});
  
  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };
  
  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({ ...prev, [folderId]: !prev[folderId] }));
  };
  
  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex gap-2 mb-4">
          <Button size="sm" onClick={onAddModule}>
            <Plus className="w-4 h-4 mr-1" /> Add Module
          </Button>
          <Button size="sm" variant="outline" onClick={onAddFolder}>
            <FolderPlus className="w-4 h-4 mr-1" /> Add Folder
          </Button>
          <Button size="sm" variant="outline" onClick={onAddContent}>
            <FileText className="w-4 h-4 mr-1" /> Add Content
          </Button>
        </div>
      )}
      
      {!workshop.modules?.length ? (
        <div className="text-center py-12 text-gray-400">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No content yet. Start by adding a module.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {workshop.modules
            .sort((a, b) => a.order - b.order)
            .map(module => (
              <ModuleItem
                key={module.id}
                module={module}
                expanded={expandedModules[module.id]}
                expandedFolders={expandedFolders}
                canEdit={canEdit}
                onToggle={() => toggleModule(module.id)}
                onToggleFolder={toggleFolder}
                onEdit={() => onEditModule(module)}
                onDelete={() => onDeleteModule(module.id)}
                onEditFolder={onEditFolder}
                onDeleteFolder={onDeleteFolder}
                onEditContent={onEditContent}
                onDeleteContent={onDeleteContent}
              />
            ))}
        </div>
      )}
    </div>
  );
};

const ModuleItem = ({
  module,
  expanded,
  expandedFolders,
  canEdit,
  onToggle,
  onToggleFolder,
  onEdit,
  onDelete,
  onEditFolder,
  onDeleteFolder,
  onEditContent,
  onDeleteContent
}) => {
  // Get direct content (content without folder)
  const directContent = module.directContent || [];
  
  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden">
      <div 
        className="flex items-center justify-between p-3 bg-slate-700 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronUp className="w-4 h-4 text-white" /> : <ChevronDown className="w-4 h-4 text-white" />}
          <span className="font-medium text-white">{module.title}</span>
          <Badge variant="secondary" className="text-xs">
            {module.folders?.length || 0} folders
          </Badge>
          {directContent.length > 0 && (
            <Badge variant="outline" className="text-xs">
              {directContent.length} direct items
            </Badge>
          )}
        </div>
        {canEdit && (
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Button size="sm" variant="ghost" onClick={onEdit}>
              <Edit className="w-3 h-3" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Module?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will delete all folders and content in this module.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
      
      {expanded && (
        <div className="p-3 space-y-2">
          {/* Direct content in module (no folder) */}
          {directContent.length > 0 && (
            <div className="mb-3 pb-3 border-b border-slate-600">
              <p className="text-xs text-gray-400 mb-2">📄 Direct Content</p>
              {directContent.sort((a, b) => a.order - b.order).map(content => (
                <ContentItem
                  key={content.id}
                  content={content}
                  folderId={null}
                  canEdit={canEdit}
                  onEdit={() => onEditContent(content, null, module.id)}
                  onDelete={() => onDeleteContent(content.id)}
                />
              ))}
            </div>
          )}
          
          {/* Folders */}
          {module.folders?.sort((a, b) => a.order - b.order).map(folder => (
            <FolderItem
              key={folder.id}
              folder={folder}
              moduleId={module.id}
              expanded={expandedFolders[folder.id]}
              canEdit={canEdit}
              onToggle={() => onToggleFolder(folder.id)}
              onEdit={() => onEditFolder(folder, module.id)}
              onDelete={() => onDeleteFolder(folder.id)}
              onEditContent={onEditContent}
              onDeleteContent={onDeleteContent}
            />
          ))}
          {!module.folders?.length && directContent.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-2">No content in this module</p>
          )}
        </div>
      )}
    </div>
  );
};

const FolderItem = ({
  folder,
  moduleId,
  expanded,
  canEdit,
  onToggle,
  onEdit,
  onDelete,
  onEditContent,
  onDeleteContent
}) => (
  <div className="border border-slate-600 rounded">
    <div 
      className="flex items-center justify-between p-2 bg-slate-600 cursor-pointer"
      onClick={onToggle}
    >
      <div className="flex items-center gap-2">
        {expanded ? <ChevronUp className="w-3 h-3 text-white" /> : <ChevronDown className="w-3 h-3 text-white" />}
        <FolderPlus className="w-4 h-4 text-cyan-400" />
        <span className="text-sm text-white">{folder.title}</span>
        <Badge variant="outline" className="text-xs">
          {folder.content?.length || 0} items
        </Badge>
      </div>
      {canEdit && (
        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={onEdit}>
            <Edit className="w-3 h-3" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400">
                <Trash2 className="w-3 h-3" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Folder?</AlertDialogTitle>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
    
    {expanded && folder.content?.length > 0 && (
      <div className="p-2 space-y-1">
        {folder.content.sort((a, b) => a.order - b.order).map(content => (
          <ContentItem
            key={content.id}
            content={content}
            folderId={folder.id}
            canEdit={canEdit}
            onEdit={() => onEditContent(content, folder.id)}
            onDelete={() => onDeleteContent(content.id)}
          />
        ))}
      </div>
    )}
  </div>
);

const ContentItem = ({ content, folderId, canEdit, onEdit, onDelete }) => (
  <div className="flex items-center justify-between p-2 bg-slate-700 rounded text-sm">
    <div className="flex items-center gap-2">
      {getContentTypeIcon(content.content_type)}
      <span className="text-white">{content.title}</span>
    </div>
    {canEdit && (
      <div className="flex gap-1">
        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={onEdit}>
          <Edit className="w-3 h-3" />
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-6 w-6 p-0 text-red-400 hover:text-red-300" 
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Content?</AlertDialogTitle>
              <AlertDialogDescription>
                Delete &quot;{content.title}&quot;? This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    )}
  </div>
);
